import { reviver } from '@thinkalpha/common/serialization/serializer.js';
import type { SocketEvents } from '@thinkalpha/ui-types/contracts/push';
import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { Decoder, Encoder } from 'socket.io-parser';
import { appConfig } from 'src/lib/config';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'push:authentication' });

export const CONNECTION_OPTIONS = {
    'force new connection': true,
    timeout: 10000,
    transports: ['websocket'],
    parser: {
        Encoder: Encoder,
        Decoder: class extends Decoder {
            constructor() {
                super(reviver);
            }
        },
    },
};

// Event map for typed events - only known message types
interface PushServiceEventMap {
    // Authentication events
    'auth.success': (payload?: any) => void;
    'auth.failed': (payload?: any) => void;

    // Socket events
    connect: () => void;
    disconnect: () => void;

    // Application events
    [SocketEvents.activeApp]: (payload: { appId: string; userId: string }) => void;
    [SocketEvents.checkAppVersion]: () => void;
}

// Emit event map - only known message types
interface PushServiceEmitMap {
    auth: (accessToken: string) => void;
}

export interface PushServiceSocket {
    on<K extends keyof PushServiceEventMap>(event: K, listener: PushServiceEventMap[K]): void;
    off<K extends keyof PushServiceEventMap>(event: K, listener: PushServiceEventMap[K]): void;
    emit<K extends keyof PushServiceEmitMap>(event: K, ...args: Parameters<PushServiceEmitMap[K]>): void;
    setAccessToken(accessToken: string): void;
}

class PushServiceSocketImpl implements PushServiceSocket {
    #socket: Socket;

    constructor() {
        this.#socket = io(appConfig.api, CONNECTION_OPTIONS);
        this.#setupEventListeners();

        // TODO: Do not use window object to detect browser/native, use `isNative`
        if (typeof window !== 'undefined') (window as any)['socket'] = this.#socket;
    }

    #setupEventListeners(): void {
        this.#socket.on('connect', () => {
            log.info({ message: 'Connected to platform socket.io' });
        });
        this.#socket.on('auth.failed', (payload: any) => {
            log.warn({ message: 'Authentication to platform socket.io failed', payload });
        });
        this.#socket.on('auth.success', (payload: any) => {
            log.info({ message: 'Authentication to platform socket.io succeeded', payload });
        });
    }

    on<K extends keyof PushServiceEventMap>(event: K, listener: PushServiceEventMap[K]): void {
        this.#socket.on(event as string, listener as any);
    }

    off<K extends keyof PushServiceEventMap>(event: K, listener: PushServiceEventMap[K]): void {
        this.#socket.off(event as string, listener as any);
    }

    emit<K extends keyof PushServiceEmitMap>(event: K, ...args: Parameters<PushServiceEmitMap[K]>): void {
        this.#socket.emit(event as string, ...args);
    }

    setAccessToken(accessToken: string): void {
        log.info({ message: 'Authenticating with socket.io', accessToken });
        this.emit('auth', accessToken);
    }
}

export const pushServiceSocket = new PushServiceSocketImpl();

// Separate socket instance for session barrier that only uses regular access tokens (no masquerading)
export const sessionBarrierSocket = new PushServiceSocketImpl();
